import { Button, ButtonProps, PolymorphicComponentProps } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconDownload } from "@tabler/icons-react"
import { Link, useRouter } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

interface Props extends Omit<
  PolymorphicComponentProps<"button", ButtonProps>,
  "ref"
> {}

export function CancelButton(props: Props) {
  const { t } = useTranslation("app_server")
  return <Button variant="default" children={t("Cancel")} {...props} />
}
/**
 * Button to save a form. Has type 'submit'
 */
export function SubmitButton(props: Omit<Props, "type">) {
  return <Button type="submit" {...props} />
}

/**
 * Button to save a form. Has type 'submit'
 */
export function SaveButton(props: Omit<Props, "type">) {
  const { t } = useTranslation("app_server")
  return <SubmitButton children={t("Save")} {...props} />
}
/**
 * Button to save a form. Has type 'submit'
 */
export function CreateButton(props: Omit<Props, "type">) {
  const { t } = useTranslation("app_server")
  return <SubmitButton children={t("Create")} {...props} />
}

export const PrimaryButton = React.forwardRef<HTMLButtonElement, Props>(
  (props: Props, ref) => <Button ref={ref} variant="filled" {...props} />
)

export const DefaultButton = (props: Props) => (
  <Button variant="default" {...props} />
)

export const DeleteButton = (props: Props) => {
  const { t } = useTranslation("app_server")
  return (
    <Button variant="light" color="red" children={t("Delete")} {...props} />
  )
}

export function DownloadButton(props: Props) {
  const { t } = useTranslation("app_server")

  return (
    <Button leftSection={<IconDownload />} {...props}>
      {t("Download")}
    </Button>
  )
}

export const DeleteButtonWithConfirmModal = ({
  confirmModalProps,
  ...props
}: Omit<Props, "onClick"> & {
  confirmModalProps?: {
    onConfirm: () => void
    confirmProps?: { loading?: boolean }
  }
}) => {
  const { t } = useTranslation("app_server")
  return (
    <Button
      variant="default"
      children={t("Delete")}
      {...props}
      onClick={() => {
        modals.openConfirmModal({
          title: t("AreYouSure"),
          centered: true,
          cancelProps: { children: t("Cancel") },
          onConfirm: confirmModalProps?.onConfirm,
          confirmProps: {
            children: t("YesDelete"),
            color: "red",
            loading: confirmModalProps?.confirmProps?.loading,
          },
        })
      }}
    />
  )
}
export const DefaultLinkButton = ({
  to,
  children,
  ...props
}: ButtonProps & { to: string }) => (
  <Button component={Link} to={to} variant="default" {...props}>
    {children}
  </Button>
)

export const DefaultBackButton = (props: Props) => {
  const router = useRouter()
  const { t } = useTranslation("app_server")
  return (
    <Button
      variant="default"
      onClick={() => router.history.back()}
      children={t("Back")}
      {...props}
    />
  )
}

export const NextButton = (props: Props) => {
  const { t } = useTranslation("app_server")
  return <Button variant="default" children={t("Next")} {...props} />
}
